import { AxiosError } from 'axios';
import { $api } from './interceptor';
import { Paginated } from 'types/paginated';
import { TVolumeUserData, User } from 'types/user';
import { toast } from 'App';
import { usersStore } from 'views/store/useUsersStore';
import { volumeStore } from 'views/store/useVolumeStore';

const UsersService = () => {
  const getPaginatedUsersList = async ({
    page = 0,
    pageSize = 10,
    searchValue,
    sortingValue,
  }: {
    page?: number;
    pageSize?: number;
    searchValue?: string;
    sortingValue: string;
  }): Promise<Paginated<User>> => {
    let search = searchValue;
    try {
      usersStore.setState({ isUsersLoading: true });
      const { data } = await $api.get<Paginated<User>>('api/users/list', {
        params: {
          search,
          page: page + 1,
          limit: pageSize,
          sortBy: sortingValue,
        },
      });

      usersStore.setState({
        users: data.data,
        totalPages: data.meta.totalPages,
        totalItems: data.meta.totalItems,
      });
      if (!search) {
        usersStore.setState({
          totalUsers: data.meta.totalItems,
        });
      }
      return data;
    } catch (e) {
      console.log(e);
      if (e instanceof AxiosError) {
        toast({
          title: 'Error',
          description: e.response?.data.message,
          status: 'error',
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error fetching user list',
          status: 'error',
          isClosable: true,
          position: 'top-right',
        });
      }
      throw e;
    } finally {
      usersStore.setState({ isUsersLoading: false });
    }
  };

  const banUser = async (userId: number) => {
    try {
      await $api.post<void>('api/admins/ban-user', {
        userId
      });
      toast({
        title: 'Success',
        description: 'The user was successfully banned',
        status: 'warning',
        position: 'top-right',
      });
    } catch (e) {
      if (e instanceof AxiosError) {
        toast({
          title: 'Error',
          description: e.response?.data.message,
          status: 'error',
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error banning user',
          status: 'error',
          isClosable: true,
          position: 'top-right',
        });
      }

    }
  }

  const unBanUser = async (userId: number) => {
    try {
      await $api.post<void>('api/admins/unban-user', {
        userId
      });
      toast({
        title: 'Success',
        description: 'The user was successfully unbanned',
        status: 'success',
        position: 'top-right',
      });
    } catch (e) {
      if (e instanceof AxiosError) {
        toast({
          title: 'Error',
          description: e.response?.data.message,
          status: 'error',
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error unbanning user',
          status: 'error',
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  }

  const getPaginatedUsersListByVolume = async ({
    page = 0,
    pageSize = 10,
    searchValue,
    sortingValue,
  }: {
    page?: number;
    pageSize?: number;
    searchValue?: string;
    sortingValue: string;
  }): Promise<Paginated<TVolumeUserData>> => {
    let search = searchValue;

    try {
      usersStore.setState({ isUsersLoading: true });
      const { data } = await $api.get<Paginated<TVolumeUserData>>('api/admins/users-list', {
        params: {
          search,
          page: page + 1,
          limit: pageSize,
          sortBy: sortingValue,
        },
      });

      volumeStore.setState({
        volumeUserData: data.data,
        totalPages: data.meta.totalPages,
        totalItems: data.meta.totalItems,
      });
      if (!search) {
        volumeStore.setState({
          totalUsers: data.meta.totalItems,
        });
      }
      return data;
    } catch (e) {
      console.log(e);
      if (e instanceof AxiosError) {
        toast({
          title: 'Error',
          description: e.response?.data.message,
          status: 'error',
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error fetching user list',
          status: 'error',
          isClosable: true,
          position: 'top-right',
        });
      }
      throw e;
    } finally {
      usersStore.setState({ isUsersLoading: false });
    }
  };

  return {
    getPaginatedUsersList,
    getPaginatedUsersListByVolume,
    banUser,
    unBanUser
  };
};

export const usersService = UsersService();
